'use client';

// A menu anchored to the button that opened it — the mechanism Dropdown (an
// option menu) and ActionMenu (a list of actions) share.
//
// The menu is portalled to `document.body` and positioned from the trigger's
// own `getBoundingClientRect()`. That is mandatory, not stylistic: every call
// site renders inside `.ed-ui-scale`, whose `transform: scale()` becomes the
// containing block for any `position: fixed` descendant and clips an in-place
// popover to a sliver (AGENTS.md; hit five times before this existed).
//
// A body portal leaves `.guided-theme`, where every `--ed-*` token is
// defined, so the menu carries that class itself (`MENU_SURFACE`): it only
// defines variables. Without it the menu's paper resolved to transparent and
// the drawing showed through every dropdown in the editor.

import { useCallback, useEffect, useLayoutEffect, useState, type RefObject } from 'react';

/** Visual gap between the trigger and the menu. */
const GAP = 6;
/** Viewport margin the menu keeps on every side. */
const EDGE = 8;
/** Below this much free space under the trigger, prefer opening upward. */
const MIN_BELOW = 140;

export interface MenuPlacement {
  left: number;
  top?: number;
  bottom?: number;
  maxHeight: number;
  minWidth: number;
}

/** The menu's own surface: the editor's theme (see file doc), the dock's
 *  paper, the top elevation. */
export const MENU_SURFACE =
  'guided-theme ed-scroll overflow-y-auto rounded-lg border border-[var(--ed-border-bright)] bg-[var(--ed-dock)] p-1 ed-elev-3';

/** Where an open menu goes, kept there on resize and scroll, and closed by a
 *  press outside it or by Esc (which hands focus back to the trigger). */
export function useAnchoredMenu(
  open: boolean,
  close: () => void,
  triggerRef: RefObject<HTMLElement | null>,
  menuRef: RefObject<HTMLElement | null>,
  { minWidth = 128, align = 'start' }: {
    minWidth?: number;
    /** Which of the trigger's edges the menu lines up with: its left
     *  (`start`), or its right (`end`, for a trigger at the right of a bar). */
    align?: 'start' | 'end';
  } = {},
): MenuPlacement | null {
  const [place, setPlace] = useState<MenuPlacement | null>(null);

  const position = useCallback(() => {
    const r = triggerRef.current?.getBoundingClientRect();
    if (!r) return;
    const below = window.innerHeight - r.bottom;
    const above = r.top;
    // Open downward only when there is genuinely room; a bar along the
    // bottom of the viewport is where most of these sit, so upward is the
    // common path rather than the fallback.
    const down = below >= MIN_BELOW || below >= above;
    const width = Math.max(r.width, minWidth);
    const edge = align === 'end' ? r.right - width : r.left;
    const left = Math.min(Math.max(EDGE, edge), Math.max(EDGE, window.innerWidth - width - EDGE));
    setPlace(down
      ? { left, top: r.bottom + GAP, maxHeight: below - GAP - EDGE, minWidth: width }
      : { left, bottom: window.innerHeight - r.top + GAP, maxHeight: above - GAP - EDGE, minWidth: width });
  }, [triggerRef, minWidth, align]);

  // Layout effect so the menu never paints at a stale position for a frame.
  useLayoutEffect(() => {
    if (open) position();
  }, [open, position]);

  useEffect(() => {
    if (!open) return;
    window.addEventListener('resize', position);
    window.addEventListener('scroll', position, true);
    return () => {
      window.removeEventListener('resize', position);
      window.removeEventListener('scroll', position, true);
    };
  }, [open, position]);

  // Dismiss on outside press. Capture phase: React flushes discrete events
  // synchronously from its own root listener, so a bubble-phase handler on
  // `document` can observe post-click state instead of the state at the
  // moment of the click.
  useEffect(() => {
    if (!open) return;
    const onDocDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      close();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // The menu's own Esc: nothing behind it (a picker, the drawing) should
      // take this one as well.
      e.stopPropagation();
      close();
      triggerRef.current?.focus();
    };
    document.addEventListener('mousedown', onDocDown, true);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', onDocDown, true);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [open, close, triggerRef, menuRef]);

  return open ? place : null;
}
